import { ElementRef, Injectable } from '@angular/core';
import { PosHttpService } from '../../services/pos.http.service';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import { FnAppSessionService, FnToasterService } from 'foundation';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DailyGraphsService {
  elementRef: any;
  dailyData: DataPoint[] = [];
  selectedEvent: string = '';
  selectedSchema: string = '';
  selectSchema: string[] = [];
  eventID : boolean = true;
  private modalRoot: am5.Root | null = null;
  private currentModalContent: HTMLElement | null = null;
  private currentChartData: ModalChartDataPoint[] = [];
  private currentBinMinutes: number = 15;
  private tabsContainer: HTMLElement | null = null;
  constructor(private posHttpSvcs: PosHttpService, private apSessionSvcs: FnAppSessionService, private toaster: FnToasterService) { }

  setChartData(data: any): void {
    this.dailyData = data;
  }

  createCharts(elementRef: ElementRef, selectedSchema: string, selectedEvent: string): void {
    this.elementRef = elementRef;
    this.selectedEvent = selectedEvent;
    this.selectedSchema = selectedSchema;
    this.selectSchema = [selectedSchema];
    const chartContainer = this.elementRef.nativeElement.querySelector('#daily_graphs');
    if (!chartContainer) {
      console.error('Chart container not found');
      return;
    }

    chartContainer.innerHTML = '';

    const chartDiv = document.createElement("div");
    chartDiv.style.width = '100%';
    chartDiv.style.height = '400px';
    chartContainer.appendChild(chartDiv);

    const root = am5.Root.new(chartDiv);
    if (root._logo) {
      root._logo.dispose();
    }

    root.container.set("background", am5.Rectangle.new(root, {
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x121418 : 0xf3f1f1),
      fillOpacity: 1
    }));

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 20,
      })
    );

    chart.children.unshift(
      am5.Label.new(root, {
        text: `${selectedEvent} (${selectedSchema})`,
        fontSize: 18,
        fill: am5.color(0xFF3333),
        textAlign: 'center',
        x: am5.percent(50),
        centerX: am5.percent(50),
        fontWeight: 'bold',
      })
    );

    const xAxisRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
    xAxisRenderer.grid.template.setAll({ visible: false });
    xAxisRenderer.labels.template.setAll({
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      rotation: -90,
    });
    xAxisRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: xAxisRenderer,
        categoryField: 'busiDate',
        tooltip: am5.Tooltip.new(root, {}),
      })
    );
    xAxis.data.setAll(this.dailyData);
    xAxis.children.push(
      am5.Label.new(root, {
        text: 'Date',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: 0,
        x: am5.percent(50),
        centerX: am5.percent(50),
        y: am5.percent(100),
        centerY: am5.percent(100),
        paddingTop: 5,
      })
    );

    const yAxisLeftRenderer = am5xy.AxisRendererY.new(root, { opposite: false });
    yAxisLeftRenderer.grid.template.setAll({ visible: false });
    yAxisLeftRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    yAxisLeftRenderer.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      visible: true,
      paddingRight: 10,
    });
    const yAxisLeft = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yAxisLeftRenderer,
        tooltip: am5.Tooltip.new(root, {}),
        min: 0,
        extraMax: 0.1,
        numberFormat: '#',
      })
    );
    yAxisLeft.children.unshift(
      am5.Label.new(root, {
        text: 'Count',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: -90,
        x: -2,
        y: am5.percent(50),
        centerY: am5.percent(50),
        centerX: am5.percent(50),
      })
    );

    const yAxisRightRenderer = am5xy.AxisRendererY.new(root, { opposite: true });
    yAxisRightRenderer.grid.template.setAll({ visible: false });
    yAxisRightRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFF3333 : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    yAxisRightRenderer.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      visible: true,
      paddingLeft: 5,
    });
    const yAxisRight = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yAxisRightRenderer,
        tooltip: am5.Tooltip.new(root, {}),
        min: 0,
        extraMax: 0.1,
        numberFormat: '#.##',
      })
    );
    yAxisRight.children.push(
      am5.Label.new(root, {
        text: 'Average Time',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: -90,
        x: 40,
        y: am5.percent(50),
        centerY: am5.percent(50),
        centerX: am5.percent(50),
      })
    );

    const barSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Count',
        xAxis: xAxis,
        yAxis: yAxisLeft,
        valueYField: 'count',
        categoryXField: 'busiDate',
        tooltip: am5.Tooltip.new(root, { labelText: 'Count: {valueY}' }),
      })
    );

barSeries.columns.template.events.on("click", (event) => {
      const dataContext = event.target.dataItem?.dataContext as DataPoint | undefined;
      const selectedDate = dataContext?.busiDate;
      if (selectedDate && selectedDate.trim() !== '') {
        this.showModalWithChart(selectedDate);
      }
    });
    const tooltip = barSeries.get("tooltip");
    if (tooltip) {
      const tooltipBackground = tooltip.get("background");
      if (tooltipBackground) {
        tooltipBackground.setAll({
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x333333 : 0xFFFFFF),
          fillOpacity: 1,
          stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
          strokeWidth: 1
        });
      }
      tooltip.label.setAll({
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000)
      });
    }

    barSeries.data.setAll(this.dailyData);

    am5plugins_exporting.Exporting.new(root, {
      menu: am5plugins_exporting.ExportingMenu.new(root, {}),
      dataSource: this.dailyData
    });

    barSeries.columns.template.setAll({
      maxWidth: 50,
      width: am5.percent(80),
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ?  0x6ac5fe : 0x87CEFA),
      strokeOpacity: 0,
    });

    barSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: '{valueY}',
          fontSize: 12,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x1488F2 : 0x000000),
          centerX: am5.percent(50),
          centerY: am5.percent(50),
          dy: -10,
          populateText: true,
        }),
      })
    );

    const lineSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Average Time',
        xAxis: xAxis,
        yAxis: yAxisRight,
        valueYField: 'avg90',
        categoryXField: 'busiDate',
        stroke: am5.color(0xFF0000),
        tooltip: am5.Tooltip.new(root, { labelText: '% Offline: {valueY}%' }),
      })
    );
    lineSeries.data.setAll(this.dailyData);
    lineSeries.strokes.template.setAll({
      strokeWidth: 3,
      stroke: am5.color(0x000000)
    });
    lineSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 0.5,
        sprite: am5.Label.new(root, {
          text: '{valueY}',
          fontSize: 12,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xf50000 : 0x000000),
          centerX: am5.percent(50),
          centerY: am5.percent(-20),
          populateText: true,
        }),
      })
    );
    lineSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 5,
          fill: am5.color(0xFF0000),
          stroke: am5.color(0xFFFFFF),
          strokeWidth: 2,
        }),
      })
    );

    const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
    cursor.lineX.set('visible', false);
    cursor.lineY.set('visible', false);

    const offlineCountMax = Math.max(...this.dailyData.map(d => d.count));
    const offlineCountMin = Math.min(...this.dailyData.map(d => d.count));
    const perOfflineMax = Math.max(...this.dailyData.map(d => d.avg90));
    const perOfflineMin = Math.min(...this.dailyData.map(d => d.avg90));
    chart.children.push(
      am5.Label.new(root, {
        text: `[#87CEFA]■[/] Offline Count: Max: ${offlineCountMax}, Min: ${offlineCountMin}    [#FF0000]—[/] % Offline: Max: ${perOfflineMax.toFixed(2)}, Min: ${perOfflineMin.toFixed(2)}`,
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x333333),
        fontWeight: 'normal',
        textAlign: 'left',
        x: am5.percent(0),
        centerX: am5.percent(0),
        paddingLeft: 10,
        paddingTop: 10,
        paddingBottom: 10,
      })
    );
  }

  getModalData(selectedDate: string): Observable<any> {
    const pktData = {
      busiDate: new Date(selectedDate).toLocaleDateString('en-US'),
      event: this.selectedEvent,
      schema: this.selectedSchema,
      isIdSelected : this.eventID
    };
    return this.posHttpSvcs.authPost('v1/ui/getMaxAndCount', 'getMaxAndCount', pktData);
  }

  generateChartData(modalData: any, binMinutes: number): ModalChartDataPoint[] {
    const chartDataModal: ModalChartDataPoint[] = [];
    const aggSize = binMinutes / 15;
    const numBins = 1440 / binMinutes;
    for (let bin = 0; bin < numBins; bin++) {
      let aggCount = 0;
      let aggTotal = 0;
      let aggMin = Infinity;
      let aggMax = -Infinity;
      for (let j = 0; j < aggSize; j++) {
        const i = bin * aggSize + j;
        if (i > 95) break;
        const count = modalData[`count${i}`] || 0;
        const total = modalData[`total${i}`] || 0;
        const min = modalData[`min${i}`] || 0;
        const max = modalData[`max${i}`] || 0;
        aggCount += count;
        aggTotal += total;
        if (count > 0) {
          aggMin = Math.min(aggMin, min);
          aggMax = Math.max(aggMax, max);
        }
      }
      const avg = aggCount > 0 ? Number((aggTotal / aggCount).toFixed(2)) : 0;
      const finalMin = (aggMin === Infinity) ? 0 : aggMin;
      const finalMax = (aggMax === -Infinity) ? 0 : aggMax;
      const startMinutes = bin * binMinutes;
      const hours = Math.floor(startMinutes / 60);
      const minutes = startMinutes % 60;
      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      chartDataModal.push({
        bin: timeString,
        count: aggCount,
        avg,
        min: finalMin,
        max: finalMax
      });
    }
    return chartDataModal;
  }
  createModalChart(chartDiv: HTMLElement, chartDataModal: ModalChartDataPoint[], binMinutes: number): void {
    this.currentChartData = chartDataModal;
    if (this.modalRoot) {
      this.modalRoot.dispose();
      this.modalRoot = null;
    }
    chartDiv.innerHTML = '';
    const root = am5.Root.new(chartDiv);
    this.modalRoot = root;
    if (root._logo) {
      root._logo.dispose();
    }
    root.container.set("background", am5.Rectangle.new(root, {
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x121418 : 0xf3f1f1),
      fillOpacity: 1
    }));
    root.setThemes([am5themes_Animated.new(root)]);
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 20,
      })
    );
    const numBins = chartDataModal.length;
    const minGridDistance = numBins > 48 ? 10 : (numBins > 24 ? 20 : 30);
    const labelRotation = numBins > 24 ? -90 : 0;
    const labelFontSize = numBins > 48 ? 10 : 12;
    const xAxisRenderer = am5xy.AxisRendererX.new(root, { minGridDistance });
    xAxisRenderer.grid.template.setAll({ visible: false });
    xAxisRenderer.labels.template.setAll({
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      rotation: labelRotation,
      fontSize: labelFontSize
    });
    xAxisRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: xAxisRenderer,
        categoryField: 'bin',
        tooltip: am5.Tooltip.new(root, {}),
      })
    );
    xAxis.data.setAll(chartDataModal);
    const yAxisLeftRenderer = am5xy.AxisRendererY.new(root, { opposite: false });
    yAxisLeftRenderer.grid.template.setAll({ visible: false });
    yAxisLeftRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    yAxisLeftRenderer.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      visible: true,
      paddingRight: 10,
    });
    const yAxisLeft = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yAxisLeftRenderer,
        tooltip: am5.Tooltip.new(root, {}),
        min: 0,
        extraMax: 0.1,
        numberFormat: '#',
      })
    );
    yAxisLeft.children.unshift(
      am5.Label.new(root, {
        text: 'Count',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: -90,
        x: -2,
        y: am5.percent(50),
        centerY: am5.percent(50),
        centerX: am5.percent(50),
      })
    );
    const yAxisRightRenderer = am5xy.AxisRendererY.new(root, { opposite: true });
    yAxisRightRenderer.grid.template.setAll({ visible: false });
    yAxisRightRenderer.setAll({
      stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFF3333 : 0x000000),
      strokeOpacity: 1,
      strokeWidth: 1,
    });
    yAxisRightRenderer.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
      visible: true,
      paddingLeft: 5,
    });
    const yAxisRight = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: yAxisRightRenderer,
        tooltip: am5.Tooltip.new(root, {}),
        min: 0,
        extraMax: 0.1,
        numberFormat: '#.##',
      })
    );
    yAxisRight.children.push(
      am5.Label.new(root, {
        text: 'Average Time',
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: -90,
        x: 40,
        y: am5.percent(50),
        centerY: am5.percent(50),
        centerX: am5.percent(50),
      })
    );
    const barSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Count',
        xAxis: xAxis,
        yAxis: yAxisLeft,
        valueYField: 'count',
        categoryXField: 'bin',
        tooltip: am5.Tooltip.new(root, {
          getFillFromSprite: false,
          autoTextColor: false,
          labelText: `
            Time    : {bin}
            Count   : {count}
            Avg     : {avg}
            Min     : {min}
            Max     : {max}
          `
        })
      })
    );
    const tooltip = barSeries.get("tooltip");
    if (tooltip) {
      const tooltipBackground = tooltip.get("background");
      if (tooltipBackground) {
        tooltipBackground.setAll({
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x333333 : 0xFFFFFF),
          fillOpacity: 1,
          stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
          strokeWidth: 1
        });
      }
      tooltip.label.setAll({
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000)
      });
    }
    barSeries.data.setAll(chartDataModal);
    const barWidth = binMinutes === 15 ? 20 : (binMinutes === 30 ? 40 : 80);
    const barWidthPercent = binMinutes === 15 ? am5.percent(80) : am5.percent(90);
    barSeries.columns.template.setAll({
      maxWidth: barWidth,
      width: barWidthPercent,
      fill: am5.color(this.apSessionSvcs.isDarkTheme() ?  0x6ac5fe : 0x87CEFA),
      strokeOpacity: 0,
    });
    barSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: '{valueY}',
          fontSize: 12,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x1488F2 : 0x000000),
          centerX: am5.percent(50),
          centerY: am5.percent(50),
          dy: -10,
          populateText: true,
        }),
      })
    );
    const lineSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Average Time',
        xAxis: xAxis,
        yAxis: yAxisRight,
        valueYField: 'avg',
        categoryXField: 'bin',
        stroke: am5.color(0xFF0000),
        tooltip: am5.Tooltip.new(root, { labelText: 'Time: {bin}\nAvg: {valueY}' }),
      })
    );
    lineSeries.data.setAll(chartDataModal);
    lineSeries.strokes.template.setAll({
      strokeWidth: 2,
      stroke: am5.color(0xFF0000),
    });
    lineSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 0.5,
        sprite: am5.Label.new(root, {
          text: '{valueY}',
          fontSize: 12,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xf50000 : 0x000000),
          centerX: am5.percent(50),
          centerY: am5.percent(-20),
          populateText: true,
        }),
      })
    );
    lineSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 5,
          fill: am5.color(0xFF0000),
          stroke: am5.color(0xFFFFFF),
          strokeWidth: 2,
        }),
      })
    );
    const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
    cursor.lineX.set('visible', false);
    cursor.lineY.set('visible', false);
    const countMax = Math.max(...chartDataModal.map(d => d.count));
    const countMin = Math.min(...chartDataModal.map(d => d.count));
    const avgMax = Math.max(...chartDataModal.map(d => d.avg));
    const avgMin = Math.min(...chartDataModal.map(d => d.avg));
    chart.children.push(
      am5.Label.new(root, {
        text: `[#87CEFA]■[/] Count: Max: ${countMax}, Min: ${countMin}    [#FF0000]—[/] Avg: Max: ${avgMax.toFixed(2)}, Min: ${avgMin.toFixed(2)}`,
        fontSize: 14,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x333333),
        fontWeight: 'normal',
        textAlign: 'left',
        x: am5.percent(0),
        centerX: am5.percent(0),
        paddingLeft: 10,
        paddingTop: 10,
        paddingBottom: 10,
      })
    );
    am5plugins_exporting.Exporting.new(root, {
      menu: am5plugins_exporting.ExportingMenu.new(root, {}),
      dataSource: chartDataModal,
    });
  }
  showModalWithChart(selectedDate: string): void {
    this.getModalData(selectedDate).subscribe((resp) => {
      if (resp.isError) {
        this.toaster.error('Invalid Data');
        return;
      }

      if (!resp.data || resp.data.length === 0) {
        this.toaster.error('No data available');
        return;
      }

      const modalData = resp.data.data[0];

      const modal = document.createElement('div');
      modal.style.position = 'fixed';
      modal.style.top = '0';
      modal.style.left = '0';
      modal.style.width = '100%';
      modal.style.height = '100%';
      modal.style.background = 'rgba(0,0,0,0.5)';
      modal.style.zIndex = '1000';
      modal.style.display = 'flex';
      modal.style.justifyContent = 'center';
      modal.style.alignItems = 'center';

      const modalContent = document.createElement('div');
      modalContent.style.background = this.apSessionSvcs.isDarkTheme() ? '#121418' : '#ffffff';
      modalContent.style.padding = '20px';
      modalContent.style.width = '80%';
      modalContent.style.height = '80%';
      modalContent.style.position = 'relative';
      modalContent.style.borderRadius = '8px';
      modalContent.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
      modalContent.style.display = 'flex';
      modalContent.style.flexDirection = 'column';
      this.currentModalContent = modalContent;

      const header = document.createElement('div');
      header.style.padding = '10px';
      header.style.borderBottom = '1px solid ' + (this.apSessionSvcs.isDarkTheme() ? '#ffffff' : '#000000');
      header.style.fontSize = '18px';
      header.style.fontWeight = 'bold';
      header.style.color = this.apSessionSvcs.isDarkTheme() ? '#ffffff' : '#000000';
      const eventLabel = 'Event :';
      const eventValue =  (Array.isArray(this.selectedEvent) ? this.selectedEvent.join(', ') : this.selectedEvent);

      header.innerHTML = `
        <span style="color: brown;">Date :</span> 
        <span style="color: blue;">${selectedDate}</span>
        &nbsp;&nbsp;&nbsp;
        <span style="color: brown;">Schema :</span> 
        <span style="color: blue;">${this.selectSchema}</span>
        &nbsp;&nbsp;&nbsp;
        <span style="color: brown;">${eventLabel}</span> 
        <span style="color: blue;">${eventValue}</span>
      `;
      modalContent.appendChild(header);

      const closeButton = document.createElement('button');
      closeButton.innerText = 'Close';
      closeButton.style.position = 'absolute';
      closeButton.style.top = '10px';
      closeButton.style.right = '10px';
      closeButton.style.padding = '5px 10px';
      closeButton.style.background = '#ff0000';
      closeButton.style.color = '#ffffff';
      closeButton.style.border = 'none';
      closeButton.style.cursor = 'pointer';
      closeButton.onclick = () => {
        if (this.modalRoot) {
          this.modalRoot.dispose();
          this.modalRoot = null;
        }
        this.tabsContainer = null;
        document.body.removeChild(modal);
      };
      modalContent.appendChild(closeButton);

      const contentDiv = document.createElement('div');
      contentDiv.style.width = '100%';
      contentDiv.style.flex = '1';
      contentDiv.style.overflow = 'auto';
      modalContent.appendChild(contentDiv);

      modal.appendChild(modalContent);
      document.body.appendChild(modal);

      this.updateModalContent(selectedDate, modalData);
    }, (error: any) => {
      this.toaster.error('Failed to load data');
    });
  }

  updateModalContent(selectedDate: string, modalData: any): void {
    if (!this.currentModalContent) return;

    const contentDiv = this.currentModalContent.querySelector('div[style*="flex: 1"]') as HTMLElement;
    if (!contentDiv) return;

    if (this.modalRoot) {
      this.modalRoot.dispose();
      this.modalRoot = null;
    }

    contentDiv.innerHTML = '';

    if (this.tabsContainer && this.tabsContainer.parentNode) {
      this.tabsContainer.parentNode.removeChild(this.tabsContainer);
      this.tabsContainer = null;
    }

    this.tabsContainer = document.createElement('div');
    this.tabsContainer.style.display = 'flex';
    this.tabsContainer.style.justifyContent = 'center';
    this.tabsContainer.style.marginBottom = '10px';
    this.currentModalContent.insertBefore(this.tabsContainer, contentDiv);

    const timeIntervals = [
      { label: '15 Min', value: 15 },
      { label: '30 Min', value: 30 },
      { label: '1 Hour', value: 60 }
    ];

    timeIntervals.forEach(interval => {
      const tabButton = document.createElement('button');
      tabButton.innerText = interval.label;
      tabButton.style.padding = '5px 15px';
      tabButton.style.margin = '0 5px';
      tabButton.style.background = this.currentBinMinutes === interval.value
        ? (this.apSessionSvcs.isDarkTheme() ? '#555555' : '#cccccc')
        : (this.apSessionSvcs.isDarkTheme() ? '#333333' : '#e0e0e0');
      tabButton.style.color = this.apSessionSvcs.isDarkTheme() ? '#ffffff' : '#000000';
      tabButton.style.border = 'none';
      tabButton.style.cursor = 'pointer';
      tabButton.style.borderRadius = '4px';
      tabButton.onclick = () => {
        this.currentBinMinutes = interval.value;
        timeIntervals.forEach(int => {
          const btn = this.tabsContainer!.querySelector(`button[data-interval="${int.value}"]`) as HTMLButtonElement;
          if (btn) {
            btn.style.background = this.currentBinMinutes === int.value
              ? (this.apSessionSvcs.isDarkTheme() ? '#555555' : '#cccccc')
              : (this.apSessionSvcs.isDarkTheme() ? '#333333' : '#e0e0e0');
          }
        });
        this.updateModalContent(selectedDate, modalData);
      };
      tabButton.setAttribute('data-interval', interval.value.toString());
      if (this.tabsContainer) {
        this.tabsContainer.appendChild(tabButton);
      }
    });

    const chartData = this.generateChartData(modalData, this.currentBinMinutes);
    this.currentChartData = chartData;

    const chartDiv = document.createElement('div');
    chartDiv.style.width = '100%';
    chartDiv.style.height = '100%';
    contentDiv.appendChild(chartDiv);

    this.createModalChart(chartDiv, chartData, this.currentBinMinutes);
  }

}

interface DataPoint {
  busiDate: string;
  count: number;
  divide: number;
  avg95: number,
  cnt95: number,
  cnt999: number,
  max: number,
  cnt90: number,
  avg90: number,
  cnt99: number,
  min: number,
  avg999: number,
  avg99: number
}

interface ModalChartDataPoint {
  bin: string;
  count: number;
  avg: number;
  min: number;
  max: number;
}
